import fp from 'lodash/fp';
import React, { FC, forwardRef } from 'react';

import { PaletteColor, Stack, Tooltip, Typography, TypographyProps, useDesktop } from 'Modules/ui3';

import { CellProps } from '../types';
import { EmissionItem, EmissionStatus } from '../../../api';


export const EmissionTypography: typeof Typography = forwardRef((props, ref) => {
    const isDesktop = useDesktop();
    return (
        <Typography
            ref={ref}
            variant={isDesktop ? 'regularBody2' : 'regularCaption3'}
            mt={0.5}
            {...props}
        />
    );
});

const DocumentLink: FC<TypographyProps<'a'>> = props => (
    <EmissionTypography
        component='a'
        target='_blank'
        sx={{
            '&:visited': {
                color: 'general.primary50',
            },
        }}
        {...props}
    />
);

export const IssuerCell: FC<CellProps> = ({ data, column: { field } }) => {
    const text = fp.get(field, data);
    return (
        <Tooltip
            text={text}
            placement='top-start'
            textProps={{
                variant: 'regularCaption2',
            }}
        >
            <EmissionTypography
                color='grayscale.shade95'
                overflow='hidden'
                textOverflow='ellipsis'
                sx={{
                    '&:hover': {
                        color: 'general.primary50',
                    },
                }}
            >
                {text}
            </EmissionTypography>
        </Tooltip>
    );
};

const statusColor: Record<EmissionItem['status'], PaletteColor> = {
    [EmissionStatus.acception]: 'infoCustom.orange60',
    [EmissionStatus.active]: 'infoCustom.green50',
    [EmissionStatus.closed]: 'grayscale.shade90',
    [EmissionStatus.other]: 'grayscale.shade90',
    [EmissionStatus.failed]: 'grayscale.shade90',
    [EmissionStatus.upcoming]: 'infoCustom.orange60',
    [EmissionStatus.processing]: 'infoCustom.orange60',
};
export const StatusCell: FC<CellProps> = ({ data, t }) => {
    const isDesktop = useDesktop();

    const color = statusColor[data.status];
    const text = data.status === EmissionStatus.other
        ? data.statusextended
        : t(`status.${data.status}`) as string;

    if (!text)
        return null;

    return (
        <Typography
            component='div'
            color={color}
            variant={isDesktop ? 'mediumCaption3' : 'mediumMicro1'}
            py={1}
            px={isDesktop ? 2 : 3}
            border='1px solid'
            borderRadius={10}
            borderColor={color}
            title={text}
        >
            {text}
        </Typography>
    );
};

export const StringCell: FC<CellProps> = ({ data, column: { field } }) => {
    const text = fp.get(field, data) as string;
    return (
        <EmissionTypography
            component='span'
            color='grayscale.shade95'
            title={text}
        >
            {text}
        </EmissionTypography>
    );
};

export const DocumentCell: FC<CellProps> = ({ data, column: { field } }) => {
    const url = fp.get(`${field}.file.url`, data);
    if (!url)
        return null;

    const text = fp.get(`${field}.caption`, data);
    return (
        <DocumentLink href={url} title={text}>
            {text}
        </DocumentLink>
    );
};

export const DecisionCell: FC<CellProps> = ({ t, ...props }) => {
    const isDesktop = useDesktop();
    const signUrl = fp.get('decision_sign.file.url', props.data);
    return (
        <Stack
            width='100%'
            {...isDesktop ? {} : {
                direction: 'row',
                justifyContent: 'space-between',
                spacing: 4,
                alignItems: 'flex-end',
            }}
        >
            <DocumentCell {...props} />
            {signUrl && (
                <DocumentLink
                    href={signUrl}
                    title={fp.get('decision_sign.caption', props.data)}
                    flexShrink={0}
                >
                    {t('sign')}
                </DocumentLink>
            )}
        </Stack>
    );
};
